/**
 * WorkspaceSwitcher — top-nav workspace-selector.
 *
 * PR-P0-2 rewrite (docs/implementation-plan.md §1.4): trigger button
 * is now the DS <WorkspacePill> (workspace-owner sessions) or
 * <ScopePill> (assessor sessions). The dropdown menu + enter-session
 * POST + MFA badge business logic is preserved from the pre-rewrite
 * version (advisor-role behaviour AR-15 etc.).
 *
 * Rendering rules (docs/users-onboarding-billing-plan.md §1.5):
 *   - Zero workspaces → render nothing.
 *   - Exactly one workspace, workspace-owner session → render the
 *     WorkspacePill flat (no dropdown chrome).
 *   - Two or more → WorkspacePill triggers a dropdown menu.
 *   - Assessor session → <ScopePill> replaces the WorkspacePill; the
 *     dropdown lists snapshot / engagement metadata rather than
 *     other workspaces (assessor can't switch to a workspace they
 *     don't have a live engagement in).
 *
 * Consultant aggregate mode (nav-ia-v2 §7.6 + §9.3), wired in PR-P3-4.
 * The pill renders its aggregate variant only when an aggregate view is
 * ACTUALLY active — not merely because the user holds ≥2 memberships.
 * The pre-P3-4 behaviour labelled the pill "3 workspaces (Aggregate)"
 * while the session sat squarely inside one client's workspace, which
 * told a consultant the opposite of the truth on the one control whose
 * entire job is answering "whose data am I looking at?".
 *
 * The dropdown's aggregate entry names what it actually does — the
 * cross-workspace ALERTS inbox — rather than promising a general
 * aggregate mode. §7.6 defers the aggregate Dashboard ("requires new
 * aggregation service"), and a menu item that implies it exists is a
 * promise the app can't keep.
 *
 * Advisor-role AR-15 preserved: rows sorted by `last_seen_at DESC`,
 * MFA badge derived from `mfa_verified_at + 12h`, `session/enter`
 * POST before navigation, `mfa_required` step-up hard-nav.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { WorkspacePill, ScopePill } from '@aigovops/design-system';
import { useOptionalWorkspace, type WorkspaceRole } from '../../lib/workspace';
import { req } from '../../lib/api';
import { hasFirmPresence } from '../../lib/firm-presence';

type Row = {
  workspace_id: string;
  slug: string;
  name: string;
  kind: 'personal' | 'team' | 'consultant';
  role: WorkspaceRole;
  mfa_verified_at?: string | null;
  last_seen_at?: string | null;
  readiness_score?: number | null;
};

type OrgRow = {
  slug: string;
  name: string;
  role: string;
};

type ListResponse = { workspaces: Row[]; organizations?: OrgRow[] };

const MFA_FRESHNESS_MS = 12 * 60 * 60 * 1000;

type MfaState = 'fresh' | 'expired' | 'never';

function mfaState(mfaVerifiedAt: string | null | undefined, nowMs: number): MfaState {
  if (!mfaVerifiedAt) return 'never';
  const t = Date.parse(mfaVerifiedAt);
  if (!Number.isFinite(t)) return 'never';
  return t + MFA_FRESHNESS_MS > nowMs ? 'fresh' : 'expired';
}

type HealthStatus = 'good' | 'warning' | 'critical' | undefined;

function healthStatusOf(score: number | null | undefined): HealthStatus {
  if (score == null) return undefined;
  if (score >= 70) return 'good';
  if (score >= 40) return 'warning';
  return 'critical';
}

const HEALTH_DOT_COLORS: Record<string, string> = {
  good: 'var(--band-green-mark)',
  warning: 'var(--band-amber-mark)',
  critical: 'var(--band-red-mark)',
};

function sortRows(rows: Row[]): Row[] {
  const withTs = rows.map((r) => ({
    row: r,
    ts: r.last_seen_at ? Date.parse(r.last_seen_at) : NaN,
  }));
  withTs.sort((a, b) => {
    const aValid = Number.isFinite(a.ts);
    const bValid = Number.isFinite(b.ts);
    if (aValid && bValid) return b.ts - a.ts;
    if (aValid) return -1;
    if (bValid) return 1;
    return a.row.slug.localeCompare(b.row.slug);
  });
  return withTs.map((x) => x.row);
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return 'WS';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export function WorkspaceSwitcher() {
  const current = useOptionalWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [orgs, setOrgs] = useState<OrgRow[]>([]);
  const [open, setOpen] = useState(false);
  const [entering, setEntering] = useState<string | null>(null);
  const [enterError, setEnterError] = useState<string | null>(null);
  const [itemCounts, setItemCounts] = useState<Map<string, number>>(new Map());
  // T6-B1: "Audit firm" for anyone who is also on an audit firm's staff.
  const [firmMember, setFirmMember] = useState(false);
  useEffect(() => {
    let live = true;
    void hasFirmPresence().then((v) => { if (live) setFirmMember(v); });
    return () => { live = false; };
  }, []);
  const triggerRef = useRef<HTMLDivElement>(null);

  // NAV-2-4 R2: Escape key closes dropdown and resets aria-expanded.
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        // Return focus to trigger for keyboard users
        const trigger = triggerRef.current?.querySelector<HTMLElement>('[aria-expanded]');
        trigger?.focus();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const body = await req<ListResponse>('/api/workspaces');
        if (cancelled) return;
        setRows(sortRows(body.workspaces ?? []));
        setOrgs(body.organizations ?? []);
      } catch {
        if (!cancelled) setRows([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!open || !rows || rows.length === 0) return;
    let cancelled = false;
    (async () => {
      const counts = new Map<string, number>();
      for (const r of rows) {
        try {
          const body = await req<{ items?: unknown[] }>(`/api/w/${encodeURIComponent(r.slug)}/register/mine`);
          if (!cancelled) {
            counts.set(r.slug, Array.isArray(body.items) ? body.items.length : Array.isArray(body) ? (body as unknown[]).length : 0);
          }
        } catch {
          // best-effort — count stays absent
        }
      }
      if (!cancelled) setItemCounts(counts);
    })();
    return () => { cancelled = true; };
  }, [open, rows]);

  async function enterAndNavigate(slug: string) {
    setEntering(slug);
    setEnterError(null);
    try {
      const res = await fetch(`/api/w/${encodeURIComponent(slug)}/session/enter`, { // raw-fetch-ok: needs raw Response for MFA step_up_url
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: '{}',
      });
      if (res.ok) {
        setOpen(false);
        navigate(`/w/${slug}/app/dashboard`);
        return;
      }
      if (res.status === 401) {
        let body: { error?: string; step_up_url?: string; detail?: string } = {};
        try {
          body = (await res.json()) as typeof body;
        } catch {
          /* ignore */
        }
        if (body.error === 'mfa_required' && body.step_up_url) {
          window.location.href = body.step_up_url;
          return;
        }
      }
      setEnterError(`Couldn't enter workspace (${res.status})`);
    } catch {
      setEnterError("Couldn't reach the server");
    } finally {
      setEntering(null);
    }
  }

  // Still loading — stable-width placeholder so header doesn't reflow.
  if (rows === null) {
    return <span aria-hidden="true" style={{ minWidth: '9rem' }} />;
  }
  if (rows.length === 0) return null;

  const currentSlug = current?.workspace.slug;
  const currentRow = rows.find((r) => r.slug === currentSlug) ?? rows[0];
  const others = rows.filter((r) => r.slug !== currentRow.slug);
  const isAssessor = current?.role === 'assessor';
  // Membership count says the aggregate view is AVAILABLE. Only the
  // active view says it's ON, and only the second of those belongs on
  // the pill.
  const canAggregate = rows.length >= 2 && !isAssessor;
  // A suffix test on the current location, not a link — the pill needs
  // to know whether the aggregate view is on, and `?view=aggregate` is
  // only meaningful on the Alerts inbox today.
  const isAggregate =
    canAggregate &&
    searchParams.get('view') === 'aggregate' &&
    location.pathname.endsWith('/alerts');
  const nowMs = Date.now();

  // Assessor session — <ScopePill> replaces the WorkspacePill entirely.
  // NAV-5-3: The pill shows engagement name + framework (not workspace
  // name). Single-engagement mode = locked pill (no dropdown chevron,
  // click does nothing). Multi-engagement mode = dropdown lists only
  // assigned engagements. Never shows the customer's other workspaces.
  if (isAssessor) {
    const scopeBar = current?.scopeBar;
    const frameworkLabel = scopeBar?.framework_name;
    const pillLabel = frameworkLabel
      ? `${currentRow.name} · ${frameworkLabel}`
      : currentRow.name;

    // Assessor engagements: filter rows to assessor-role only.
    const assessorEngagements = rows.filter((r) => r.role === 'assessor');
    const otherEngagements = assessorEngagements.filter(
      (r) => r.slug !== currentRow.slug,
    );
    const hasMultipleEngagements = otherEngagements.length > 0;

    return (
      <div ref={triggerRef} style={{ position: 'relative' }}>
        <ScopePill
          workspace={pillLabel}
          snapshotId={scopeBar?.snapshot_id}
          expanded={hasMultipleEngagements ? open : undefined}
          onClick={hasMultipleEngagements ? () => setOpen((v) => !v) : undefined}
        />
        {open && hasMultipleEngagements ? (
          <>
            <div
              onClick={() => setOpen(false)}
              aria-hidden="true"
              style={{ position: 'fixed', inset: 0, background: 'transparent', zIndex: 10 }}
            />
            <ul
              role="menu"
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: 'var(--sp-1)',
                minWidth: '18rem',
                background: 'var(--surface-elevated)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                boxShadow: 'var(--shadow-md)',
                padding: 'var(--sp-1) 0',
                margin: 0,
                listStyle: 'none',
                zIndex: 11,
              }}
            >
              {/* Current engagement header */}
              <li role="none">
                <div
                  style={{
                    padding: 'var(--sp-2) var(--sp-3)',
                    fontSize: 'var(--fs-sm)',
                    color: 'var(--text-muted)',
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  <div style={{ fontWeight: 500, color: 'var(--text)' }}>{pillLabel}</div>
                  <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
                    Current engagement
                  </div>
                </div>
              </li>

              {/* Section label */}
              <li
                role="none"
                style={{
                  padding: 'var(--sp-1) var(--sp-3)',
                  fontSize: 'var(--fs-xs)',
                  color: 'var(--text-muted)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Other Engagements
              </li>

              {/* Other assessor engagements */}
              {otherEngagements.map((w) => {
                const isEntering = entering === w.slug;
                return (
                  <li key={w.workspace_id} role="none">
                    <button
                      role="menuitem"
                      type="button"
                      disabled={entering !== null}
                      onClick={() => enterAndNavigate(w.slug)}
                      style={{
                        display: 'block',
                        width: '100%',
                        background: 'transparent',
                        border: 0,
                        textAlign: 'left',
                        padding: 'var(--sp-2) var(--sp-3)',
                        font: 'inherit',
                        color: 'inherit',
                        cursor: entering !== null ? 'wait' : 'pointer',
                        opacity: entering !== null && !isEntering ? 0.5 : 1,
                      }}
                    >
                      <span style={{ fontWeight: 500 }}>{w.name}</span>
                      <span
                        style={{
                          display: 'block',
                          fontSize: 'var(--fs-xs)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        assessor{isEntering ? ' · entering…' : ''}
                      </span>
                    </button>
                  </li>
                );
              })}

              {enterError ? (
                <li role="none" aria-live="polite">
                  <p
                    style={{
                      margin: 0,
                      padding: 'var(--sp-2) var(--sp-3)',
                      fontSize: 'var(--fs-xs)',
                      color: 'var(--band-red)',
                    }}
                  >
                    {enterError}
                  </p>
                </li>
              ) : null}
            </ul>
          </>
        ) : null}
      </div>
    );
  }

  // V3-8-1: readiness_score is now included in the /api/workspaces
  // response. Health dots (green/amber/red) are wired inline below.
  // When migrating to the DS <WorkspaceMenu>, pass `healthStatus` per
  // workspace instead (the component already supports it).

  // Workspace-owner session — <WorkspacePill>. Single workspace = flat
  // pill; two+ = dropdown listing the other memberships.
  //
  // NAV-2-4 future: replace the inline <ul role="menu"> dropdown below
  // with the DS <WorkspaceMenu> component. The integration is deferred
  // because WorkspaceMenu doesn't yet support the business logic this
  // switcher renders inline: MFA badges per row, `session/enter` POST
  // with step-up redirect, aggregate-mode toggle, per-workspace item
  // counts, and entering/error states. The plan is to extend
  // WorkspaceMenu's slot/render-prop API in a follow-up so each row can
  // carry those affordances, then swap. V3-8-2: Organization data is
  // now included in the /api/workspaces response and rendered as an
  // "Organization" section above the workspace list in the dropdown.
  return (
    <div ref={triggerRef} style={{ position: 'relative' }}>
      <WorkspacePill
        label={isAggregate ? `All my workspaces (${rows.length})` : currentRow.name}
        swatch={initialsOf(currentRow.name)}
        aggregate={isAggregate}
        count={isAggregate ? rows.length : undefined}
        expanded={open}
        onClick={() => (rows.length > 1 || orgs.length > 0) && setOpen((v) => !v)}
      />
      {open && (others.length > 0 || canAggregate || orgs.length > 0) ? (
        <>
          <div
            onClick={() => setOpen(false)}
            aria-hidden="true"
            style={{ position: 'fixed', inset: 0, background: 'transparent', zIndex: 10 }}
          />
          <ul
            role="menu"
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: 'var(--sp-1)',
              minWidth: '18rem',
              background: 'var(--surface-elevated)',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-md)',
              padding: 'var(--sp-1) 0',
              margin: 0,
              listStyle: 'none',
              zIndex: 11,
            }}
          >
            {/* Aggregate entry first — it's the mode switch, and the
                rows below it are all "leave aggregate, go to one
                client". Labelled for what it actually opens (§7.6
                defers the aggregate Dashboard), and it's a plain
                in-app navigation, not a `session/enter` POST: the
                cross-workspace read is authorized per membership
                server-side and doesn't change which workspace the
                session is parked in. */}
            {canAggregate ? (
              <li role="none">
                <button
                  role="menuitem"
                  type="button"
                  disabled={entering !== null}
                  onClick={() => {
                    setOpen(false);
                    navigate(
                      isAggregate
                        ? `/w/${currentRow.slug}/app/alerts`
                        : `/w/${currentRow.slug}/app/alerts?view=aggregate`
                    );
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    background: 'transparent',
                    border: 0,
                    borderBottom: '1px solid var(--border)',
                    textAlign: 'left',
                    padding: 'var(--sp-2) var(--sp-3)',
                    font: 'inherit',
                    color: 'inherit',
                    cursor: entering !== null ? 'wait' : 'pointer',
                  }}
                >
                  <span style={{ fontWeight: 500 }}>
                    {isAggregate
                      ? `Alerts for ${currentRow.name} only`
                      : `Alerts across all clients`}
                  </span>
                  <span
                    style={{
                      display: 'block',
                      fontSize: 'var(--fs-xs)',
                      color: 'var(--text-muted)',
                    }}
                  >
                    {isAggregate ? 'Leave the combined inbox' : 'One combined inbox'}
                  </span>
                </button>
              </li>
            ) : null}
            {orgs.length > 0 ? (
              <>
                <li
                  role="none"
                  style={{
                    padding: 'var(--sp-1) var(--sp-3)',
                    fontSize: 'var(--fs-xs)',
                    color: 'var(--text-muted)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Organization
                </li>
                {orgs.map((o) => (
                  <li key={o.slug} role="none">
                    <button
                      role="menuitem"
                      type="button"
                      disabled={entering !== null}
                      onClick={() => {
                        setOpen(false);
                        navigate(`/o/${o.slug}/dashboard`);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--sp-2)',
                        width: '100%',
                        background: 'transparent',
                        border: 0,
                        textAlign: 'left',
                        padding: 'var(--sp-2) var(--sp-3)',
                        font: 'inherit',
                        color: 'inherit',
                        cursor: entering !== null ? 'wait' : 'pointer',
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '24px',
                          height: '24px',
                          borderRadius: 'var(--radius-sm)',
                          background: 'var(--band-green-mark)',
                          color: 'var(--text-inverse)',
                          fontSize: 'var(--fs-xs)',
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {o.name.trim().slice(0, 2).toUpperCase()}
                      </span>
                      <span style={{ fontWeight: 500, flex: 1 }}>{o.name}</span>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '1px var(--sp-1)',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: 'var(--fs-xs)',
                          fontWeight: 600,
                          letterSpacing: '0.02em',
                          background: 'var(--band-green-bg)',
                          color: 'var(--band-green)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        ORG
                      </span>
                    </button>
                  </li>
                ))}
                <li
                  role="none"
                  aria-hidden="true"
                  style={{
                    borderBottom: '1px solid var(--border)',
                    margin: 'var(--sp-1) 0',
                  }}
                />
              </>
            ) : null}
            {others.length > 0 && others.some((w) => w.kind === 'consultant') ? (
              <li role="none" style={{ padding: 'var(--sp-1) var(--sp-3)', fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Workspaces
              </li>
            ) : null}
            {others.map((w) => {
              const state = mfaState(w.mfa_verified_at, nowMs);
              const health = healthStatusOf(w.readiness_score);
              const isEntering = entering === w.slug;
              return (
                <li key={w.workspace_id} role="none">
                  <button
                    role="menuitem"
                    type="button"
                    disabled={entering !== null}
                    onClick={() => enterAndNavigate(w.slug)}
                    style={{
                      display: 'block',
                      width: '100%',
                      background: 'transparent',
                      border: 0,
                      textAlign: 'left',
                      padding: 'var(--sp-2) var(--sp-3)',
                      font: 'inherit',
                      color: 'inherit',
                      cursor: entering !== null ? 'wait' : 'pointer',
                      opacity: entering !== null && !isEntering ? 0.5 : 1,
                    }}
                  >
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--sp-2)',
                      }}
                    >
                      <span style={{ fontWeight: 500, flex: 1 }}>
                        {w.name}
                        {w.kind === 'consultant' ? (
                          <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', fontWeight: 400, marginLeft: 'var(--sp-1)' }}>client</span>
                        ) : w.kind === 'personal' ? (
                          <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', fontWeight: 400, marginLeft: 'var(--sp-1)' }}>personal</span>
                        ) : null}
                      </span>
                      {health ? (
                        <span
                          aria-label={`Readiness: ${health}`}
                          title={`Readiness score: ${Math.round(w.readiness_score!)}%`}
                          style={{
                            display: 'inline-block',
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: HEALTH_DOT_COLORS[health],
                            flexShrink: 0,
                          }}
                        />
                      ) : null}
                      {itemCounts.has(w.slug) && itemCounts.get(w.slug)! > 0 ? (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '20px',
                            height: '20px',
                            padding: '0 var(--sp-1)',
                            borderRadius: '999px',
                            fontSize: 'var(--fs-xs)',
                            fontWeight: 700,
                            fontVariantNumeric: 'tabular-nums',
                            background: 'var(--band-red-bg)',
                            color: 'var(--band-red)',
                          }}
                          title={`${itemCounts.get(w.slug)} items assigned to you`}
                        >
                          {itemCounts.get(w.slug)}
                        </span>
                      ) : null}
                      <MfaBadge state={state} />
                    </span>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 'var(--fs-xs)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      {w.role}
                      {w.kind === 'personal' ? ' · Personal' : ''}
                      {isEntering ? ' · entering…' : ''}
                    </span>
                  </button>
                </li>
              );
            })}
            {firmMember ? (
              <>
                <li role="none" aria-hidden="true" style={{ borderBottom: '1px solid var(--border)', margin: 'var(--sp-1) 0' }} />
                <li role="none">
                  <button
                    role="menuitem"
                    type="button"
                    onClick={() => { setOpen(false); navigate('/audit-firm'); }}
                    style={{ display: 'block', width: '100%', background: 'transparent', border: 0, textAlign: 'left', padding: 'var(--sp-2) var(--sp-3)', font: 'inherit', color: 'inherit', cursor: 'pointer', minHeight: 44 }}
                  >
                    <span style={{ fontWeight: 500 }}>Audit firm</span>
                    <span style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>Your engagements and staff</span>
                  </button>
                </li>
              </>
            ) : null}
            {enterError ? (
              <li role="none" aria-live="polite">
                <p
                  style={{
                    margin: 0,
                    padding: 'var(--sp-2) var(--sp-3)',
                    fontSize: 'var(--fs-xs)',
                    color: 'var(--band-red)',
                  }}
                >
                  {enterError}
                </p>
              </li>
            ) : null}
          </ul>
        </>
      ) : null}
    </div>
  );
}

function MfaBadge({ state }: { state: MfaState }) {
  const styles: Record<MfaState, { bg: string; fg: string; label: string; title: string }> = {
    fresh: {
      bg: 'var(--band-green-mark)',
      fg: 'var(--text-inverse)',
      label: 'MFA fresh',
      title: 'MFA verified within the last 12 hours',
    },
    expired: {
      bg: 'var(--band-amber-mark)',
      fg: 'var(--text-inverse)',
      label: 'MFA expired',
      title: 'MFA older than 12 hours — you may be asked to verify again',
    },
    never: {
      bg: 'var(--surface-alt)',
      fg: 'var(--text-muted)',
      label: 'MFA required',
      title: 'You will be asked to verify MFA when entering this workspace',
    },
  };
  const s = styles[state];
  return (
    <span
      title={s.title}
      style={{
        display: 'inline-block',
        padding: '1px var(--sp-2)',
        borderRadius: 999,
        fontSize: 'var(--fs-xs)',
        fontWeight: 600,
        letterSpacing: '0.02em',
        background: s.bg,
        color: s.fg,
        whiteSpace: 'nowrap',
      }}
    >
      {s.label}
    </span>
  );
}
